import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkspaceResponse } from "@joogoos/contracts";
import { api, page } from "../api.js";
import { inWorkspace, pathInWorkspace } from "./address.js";

/**
 * The workspace, asked for once however many things on screen want it.
 *
 * Three do: the credits in the top bar, the account dialog, and the picker that says which assistant answers. They
 * used to declare the query separately, and a second observer mounting against data already fetched asks again —
 * which the round-trip budget caught the moment the picker was added (docs/17 §performance 4).
 *
 * Half a minute of staleness is right for what this holds: a name, a balance and a setting. Anything that moves
 * the balance invalidates the key and every observer refetches at once, so nothing here goes quietly out of date.
 */
export const useWorkspace = () => useQuery({ queryKey: ["workspace"], queryFn: api.workspace, staleTime: 30_000 });

/** Every workspace this person has. Asked for only where it is drawn — the open menu and the Workspace panel. */
export const useWorkspaces = () => useQuery({ queryKey: ["workspaces"], queryFn: api.workspaces, staleTime: 30_000 });

/**
 * Going to another workspace, or to a new one (ADR 0006 amendment). Both end the same way: the whole app again.
 * Every read cached on this page belongs to the business being left, and invalidating them one by one is a list that
 * somebody forgets to extend; a reload is the one answer that cannot leave the old business on screen.
 */
export const useSwitchWorkspace = () => useMutation({
  mutationFn: api.switchWorkspace,
  // To the other workspace's own address, on the same tab (ADR 0052). The switch also makes it the default a new tab
  // opens in; a tab already working in another workspace is not moved.
  onSuccess: (_answer: void, id: string) => page.go(inWorkspace(id, pathInWorkspace(location.pathname))),
});
/**
 * A new one lands on its Business tab rather than on whatever tab the old one was showing (2026-09-23): the first
 * thing a new business needs is to say who it is, and a reload would have kept the address — `/studio`, say — of
 * the business being left.
 */
export const useCreateWorkspace = () => useMutation({ mutationFn: api.createWorkspace, onSuccess: (made) => page.go(inWorkspace(made.id, "/business")) });

/**
 * A new name on one of them, from either place the list is drawn (2026-09-20). Nothing else moves, so nothing else is
 * thrown away: the two lists that hold a name are asked again, and the person stays on the screen they were on.
 */
export const useRenameWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.renameWorkspace,
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["workspaces"] }), qc.invalidateQueries({ queryKey: ["workspace"] })]),
  });
};

/**
 * Closing (ADR 0022). The one on screen ends the same way switching does — the whole app again, because the business
 * it is drawing is one this session can no longer be in. Another of the person's ends as a list that is one shorter,
 * with the dialog still open where they left it.
 */
export const useCloseWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.closeWorkspace,
    onSuccess: (_answer, { id }) => {
      // The one on screen: to the default, since this tab's address now names a workspace nobody can open.
      if ((qc.getQueryData(["workspace"]) as WorkspaceResponse | undefined)?.id === id) return page.go("/");
      return qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};
